import React, { useState, useEffect } from "react";
import { 
  Form, 
  Button, 
  Container, 
  Row, 
  Col, 
  Table, 
  Modal, 
  Card, 
  Spinner,
  Badge,
  Tabs,
  Tab,
  InputGroup,
  Alert,
  OverlayTrigger,
  Tooltip,
  Pagination
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import CarModelManager from "../../../Components/CarModelManager";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaImage, 
  FaVideo, 
  FaTags, 
  FaCar, 
  FaBuilding, 
  FaCheckCircle,
  FaInfoCircle,
  FaUpload,
  FaPlay
} from "react-icons/fa";

// Define API base URL to match what's used in AllProducts
const API_BASE_URL = "https://motor-easy.clickbooster.io/api";

const AddImages = () => {
    const services = [
        "Used Car Warranty",
        "Vehicle Inspection",
        "Vehicle Service",
        "Road Side",
        "Battery Replacement",
        "Windscreen Repair",
        "Home",
    ];

    const [selectedService, setSelectedService] = useState(services[0]);
    const [cars, setCars] = useState([]);
    const [makers, setMakers] = useState([]);
    const [selectedCar, setSelectedCar] = useState("");
    const [selectedMaker, setSelectedMaker] = useState("");
    const [models, setModels] = useState([]);
    const [selectedCarModel, setSelectedCarModel] = useState("");
    const [planName, setPlanName] = useState("");
    const [planDuration, setPlanDuration] = useState("");
    const [price, setPrice] = useState("");
    const [pricing, setPricing] = useState([]);
    const [editingPrice, setEditingPrice] = useState(null);
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [files, setFiles] = useState([]);
    const [videoFiles, setVideoFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [activeTab, setActiveTab] = useState("form");
    
    // State for modals
    const [showModal, setShowModal] = useState(false);
    const [showModelManagerModal, setShowModelManagerModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteType, setDeleteType] = useState(""); // "price", "image", or "video"
    const [modalData, setModalData] = useState({ planName: "", planDuration: "", price: "" });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [currentImagePage, setCurrentImagePage] = useState(1);
    const [currentVideoPage, setCurrentVideoPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchCars();
    }, []);

    useEffect(() => {
        if (selectedCar) {
            fetchMakers();
        }
    }, [selectedCar]);

    useEffect(() => {
        if (selectedCar && selectedMaker) {
            fetchModels();
        }
    }, [selectedCar, selectedMaker]);

    useEffect(() => {
        if (selectedCar && selectedMaker && selectedCarModel) {
            fetchImagesAndVideos();
            fetchPricing();
        }
    }, [selectedCar, selectedMaker, selectedCarModel]);

    const fetchCars = async () => {
        try {
            setDataLoading(true);
            const response = await fetch(`${API_BASE_URL}/cars`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setCars(data);
        } catch (error) {
            console.error("Failed to fetch cars", error.message);
            toast.error("Failed to fetch cars");
        } finally {
            setDataLoading(false);
        }
    };

    const fetchMakers = async () => {
        try {
            setDataLoading(true);
            const response = await fetch(`${API_BASE_URL}/maker?car_id=${selectedCar}`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            if (data && Array.isArray(data.makers)) {
                setMakers(data.makers);
            } else {
                console.error("Unexpected response data format:", data);
                toast.error("Unexpected data format received from server");
            }
        } catch (error) {
            console.error("Error fetching makers:", error);
            toast.error("Failed to fetch car makers");
        } finally {
            setDataLoading(false);
        }
    };

    const fetchModels = async () => {
        try {
            setDataLoading(true);
            const response = await fetch(`${API_BASE_URL}/models?car_id=${selectedCar}&maker_id=${selectedMaker}`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setModels(data);
        } catch (error) {
            console.error("Error fetching models:", error);
            toast.error("Failed to fetch car models");
        } finally {
            setDataLoading(false);
        }
    };

    const fetchImagesAndVideos = async () => {
        try {
            setDataLoading(true);
            const response = await fetch(`${API_BASE_URL}/get-car-img`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    car_id: selectedCar,
                    maker_id: selectedMaker,
                    model_id: selectedCarModel,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (data.status === 200) {
                const imageList = data.all_images.map((img) => ({
                    ...img,
                    url: `${data.imagepath}${img.car_image}`,
                }));
                setImages(imageList);

                const videoList = data.all_videos.map((video) => ({
                    ...video,
                    url: `${data.videopath}${video.car_video}`,
                }));
                setVideos(videoList);
                
                // Switch to assets tab if images or videos are available
                if (imageList.length > 0 || videoList.length > 0) {
                    setActiveTab("assets");
                }
            } else {
                // throw new Error(data.message || "Failed to fetch images and videos.");
            }
        } catch (error) {
            console.error("Error fetching images and videos:", error);
            // toast.error("Failed to fetch images and videos");
        } finally {
            setDataLoading(false);
        }
    };

    const fetchPricing = async () => {
        try {
            setDataLoading(true);
            const response = await fetch(`${API_BASE_URL}/get-car-model-price/${selectedCarModel}`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            if (data.status === 200) {
                setPricing(data.model_prices);
                
                // Switch to pricing tab if pricing is available
                if (data.model_prices.length > 0) {
                    setActiveTab("pricing");
                }
            } else {
                throw new Error("Failed to fetch pricing.");
            }
        } catch (error) {
            console.error("Error fetching pricing:", error);
            toast.error("Failed to fetch pricing");
        } finally {
            setDataLoading(false);
        }
    };

    const handleServiceChange = (e) => {
        setSelectedService(e.target.value);
    };

    const handleFileChange = (e) => {
        const filesArray = Array.from(e.target.files);
        setFiles(filesArray);

        // Create preview URLs
        const urls = filesArray.map((file) => URL.createObjectURL(file));
        setPreviewUrls(urls);
    };

    const handleVideoChange = (e) => {
        const videoArray = Array.from(e.target.files);
        const validVideos = videoArray.filter((video) => {
            const fileType = video.type;
            return fileType === "video/mp4" || fileType === "video/avi" || fileType === "video/mkv";
        });

        if (validVideos.length !== videoArray.length) {
            toast.error("Only MP4, AVI, or MKV videos are allowed");
        }
        setVideoFiles(validVideos);
    };

    const validatePricingForm = () => {
        if (!planName || !planDuration || !price) {
            toast.error("Please fill in all pricing fields");
            return false;
        }
        
        if (isNaN(price) || parseFloat(price) <= 0) {
            toast.error("Price must be a valid number greater than zero");
            return false;
        }
        
        return true;
    };

    const handleAddPricing = () => {
        if (!validatePricingForm()) return;

        setPricing((prevPricing) => [
            ...prevPricing,
            { plan_name: planName, plan_duration: planDuration, price, id: Date.now() },
        ]);

        toast.success("Pricing plan added to list");
        
        // Clear pricing form
        setPlanName("");
        setPlanDuration("");
        setPrice("");
    };

    const validateSubmitForm = () => {
        if (!selectedCar) {
            setFormError("Please select a car");
            return false;
        }
        
        if (!selectedMaker) {
            setFormError("Please select a car maker");
            return false;
        }
        
        if (!selectedCarModel) {
            setFormError("Please select a car model");
            return false;
        }
        
        if (files.length === 0 && videoFiles.length === 0 && pricing.length === 0) {
            setFormError("Please add at least one image, video, or pricing plan");
            return false;
        }
        
        setFormError("");
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateSubmitForm()) {
            return;
        }

        try {
            setFormLoading(true);
            
            // Upload images and videos if available
            if (files.length > 0 || videoFiles.length > 0) {
                const formData = new FormData();
                formData.append("car_id", selectedCar);
                formData.append("maker_id", selectedMaker);
                formData.append("model_id", selectedCarModel);

                files.forEach((file) => {
                    formData.append("car_images[]", file);
                });

                videoFiles.forEach((file) => {
                    formData.append("car_videos[]", file);
                });

                const imageResponse = await fetch(`${API_BASE_URL}/store-car-img`, {
                    method: 'POST',
                    body: formData,
                });

                if (!imageResponse.ok) {
                    throw new Error(`HTTP error! Status: ${imageResponse.status}`);
                }

                const imageData = await imageResponse.json();
                if (imageData.status === 200) {
                    toast.success("Images and videos uploaded successfully!");
                } else {
                    throw new Error("Failed to store car images and videos.");
                }
            }

            // Upload pricing if available
            if (pricing.length > 0) {
                const pricingData = pricing.map((priceItem) => ({
                    plan_name: priceItem.plan_name,
                    plan_duration: priceItem.plan_duration,
                    price: priceItem.price,
                }));

                const pricingResponse = await fetch(`${API_BASE_URL}/store-car-model-price`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model_id: selectedCarModel,
                        plan_name: pricingData.map((item) => item.plan_name),
                        plan_duration: pricingData.map((item) => item.plan_duration),
                        price: pricingData.map((item) => item.price),
                    }),
                });

                if (!pricingResponse.ok) {
                    throw new Error(`HTTP error! Status: ${pricingResponse.status}`);
                }

                const priceData = await pricingResponse.json();
                if (priceData.status === 200) {
                    toast.success("Pricing plans uploaded successfully!");
                } else {
                    throw new Error("Failed to store car pricing.");
                }
            }

            // Reset all form fields
            setFiles([]);
            setVideoFiles([]);
            setPreviewUrls([]);
            setPricing([]);

            // Refresh data
            fetchImagesAndVideos();
            fetchPricing();
            
            // Switch to assets tab to show the newly uploaded content
            setActiveTab("assets");

        } catch (error) {
            console.error("Error uploading data:", error);
            toast.error("Failed to upload data. Please try again.");
        } finally {
            setFormLoading(false);
        }
    };

    const handleShowDeleteConfirm = (item, type) => {
        setItemToDelete(item);
        setDeleteType(type);
        setShowDeleteModal(true);
    };

    const handleDeleteItem = async () => {
        try {
            setLoading(true);
            
            if (deleteType === "price") {
                const response = await fetch(`${API_BASE_URL}/deletecarmodelprice/${itemToDelete.id}`, {
                    method: 'DELETE',
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                toast.success("Pricing plan deleted successfully!");
                fetchPricing();
            } else if (deleteType === "image") {
                const response = await fetch(`${API_BASE_URL}/deletecarimg/${itemToDelete.id}`, {
                    method: 'DELETE',
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                toast.success("Image deleted successfully!");
                setImages((prevImages) => prevImages.filter((image) => image.id !== itemToDelete.id));
            } else if (deleteType === "video") {
                // Add API endpoint for video deletion when available
                const response = await fetch(`${API_BASE_URL}/deletecarvideo/${itemToDelete.id}`, {
                    method: 'DELETE',
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                toast.success("Video deleted successfully!");
                setVideos((prevVideos) => prevVideos.filter((video) => video.id !== itemToDelete.id));
            }
            
            setShowDeleteModal(false);
            setItemToDelete(null);
            
        } catch (error) {
            console.error(`Error deleting ${deleteType}:`, error);
            toast.error(`Failed to delete ${deleteType}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEditPricing = (price) => {
        setModalData({
            planName: price.plan_name,
            planDuration: price.plan_duration,
            price: price.price,
        });
        setEditingPrice(price);
        setShowModal(true);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setModalData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleModalSubmit = async () => {
        try {
            setLoading(true);
            
            const updatedPricing = {
                plan_name: modalData.planName,
                plan_duration: modalData.planDuration,
                price: modalData.price,
            };

            const response = await fetch(`${API_BASE_URL}/update-car-model-price/${editingPrice.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedPricing),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            toast.success("Pricing updated successfully!");
            fetchPricing();
            setShowModal(false);
            
        } catch (error) {
            console.error("Error updating pricing:", error);
            toast.error("Failed to update pricing");
        } finally {
            setLoading(false);
        }
    };

    const handleCarModelChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedCarModel(selectedValue);

        if (selectedValue === "other") {
            handleModelNotAvailable();
        }
    };

    const handleModelNotAvailable = () => {
        setShowModelManagerModal(true);
    };

    // Pagination for pricing
    const indexOfLastPrice = currentPage * itemsPerPage;
    const indexOfFirstPrice = indexOfLastPrice - itemsPerPage;
    const currentPricing = pricing.slice(indexOfFirstPrice, indexOfLastPrice);
    const totalPricePages = Math.ceil(pricing.length / itemsPerPage);
    
    // Pagination for images
    const indexOfLastImage = currentImagePage * itemsPerPage;
    const indexOfFirstImage = indexOfLastImage - itemsPerPage;
    const currentImages = images.slice(indexOfFirstImage, indexOfFirstImage + itemsPerPage);
    const totalImagePages = Math.ceil(images.length / itemsPerPage);
    
    // Pagination for videos
    const indexOfLastVideo = currentVideoPage * itemsPerPage;
    const indexOfFirstVideo = indexOfLastVideo - itemsPerPage;
    const currentVideos = videos.slice(indexOfFirstVideo, indexOfFirstVideo + itemsPerPage);
    const totalVideoPages = Math.ceil(videos.length / itemsPerPage);

    // Render pagination
    const renderPagination = (currentPage, totalPages, setPage) => {
        if (totalPages <= 1) return null;
        
        return (
            <Pagination className="mt-3 justify-content-center">
                <Pagination.First onClick={() => setPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} />
                
                {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                        key={index + 1}
                        active={currentPage === index + 1}
                        onClick={() => setPage(index + 1)}
                    >
                        {index + 1}
                    </Pagination.Item>
                ))}
                
                <Pagination.Next onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        );
    };

    return (
        <div className="admin-dashboard d-flex flex-column min-vh-100">
            <ToastContainer position="top-right" autoClose={3000} />
            <AdminHeader />
            
            <div className="d-flex flex-grow-1">
                <Sidebar />
                
                <main className="flex-grow-1 bg-light">
                    <Container fluid className="py-4">
                        <Card className="shadow-sm border-0 mb-4">
                            <Card.Header className="bg-white py-3">
                                <h5 className="mb-0 font-weight-bold text-primary">
                                    <FaCar className="me-2" /> Manage Car Assets and Pricing
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k)}
                                    className="mb-4"
                                >
                                    <Tab eventKey="form" title={<span><FaPlus className="me-2" />Add Content</span>}>
                                        <Card className="border-0 shadow-sm">
                                            <Card.Body>
                                                {formError && (
                                                    <Alert variant="danger" onClose={() => setFormError("")} dismissible>
                                                        {formError}
                                                    </Alert>
                                                )}
                                                
                                                <Form onSubmit={handleSubmit}>
                                                    <Row className="mb-3">
                                                        <Col md={4}>
                                                            <Form.Group controlId="serviceType">
                                                                <Form.Label className="fw-bold">Service Type</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text><FaTags /></InputGroup.Text>
                                                                    <Form.Select 
                                                                        value={selectedService} 
                                                                        onChange={handleServiceChange}
                                                                    >
                                                                        {services.map((service, index) => (
                                                                            <option key={index} value={service}>
                                                                                {service}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group controlId="car">
                                                                <Form.Label className="fw-bold">
                                                                    Car Type <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text><FaCar /></InputGroup.Text>
                                                                    <Form.Select 
                                                                        value={selectedCar} 
                                                                        onChange={(e) => setSelectedCar(e.target.value)}
                                                                        disabled={dataLoading}
                                                                    >
                                                                        <option value="">Select Car</option>
                                                                        {cars.map((car) => (
                                                                            <option key={car.id} value={car.id}>
                                                                                {car.car_name}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                    {dataLoading && <InputGroup.Text><Spinner animation="border" size="sm" /></InputGroup.Text>}
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group controlId="maker">
                                                                <Form.Label className="fw-bold">
                                                                    Manufacturer <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text><FaBuilding /></InputGroup.Text>
                                                                    <Form.Select 
                                                                        value={selectedMaker} 
                                                                        onChange={(e) => setSelectedMaker(e.target.value)}
                                                                        disabled={!selectedCar || dataLoading}
                                                                    >
                                                                        <option value="">Select Maker</option>
                                                                        {makers.map((maker) => (
                                                                            <option key={maker.id} value={maker.id}>
                                                                                {maker.maker_name}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                    {dataLoading && <InputGroup.Text><Spinner animation="border" size="sm" /></InputGroup.Text>}
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    
                                                    <Row className="mb-4">
                                                        <Col md={6}>
                                                            <Form.Group controlId="carModel">
                                                                <Form.Label className="fw-bold">
                                                                    Car Model <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text><FaCar /></InputGroup.Text>
                                                                    <Form.Select 
                                                                        value={selectedCarModel} 
                                                                        onChange={handleCarModelChange}
                                                                        disabled={!selectedMaker || dataLoading}
                                                                    >
                                                                        <option value="">Select Car Model</option>
                                                                        {models.map((model) => (
                                                                            <option key={model.id} value={model.id}>
                                                                                {model.model_name}
                                                                            </option>
                                                                        ))}
                                                                        <option value="other">Add New Model</option>
                                                                    </Form.Select>
                                                                    {dataLoading && <InputGroup.Text><Spinner animation="border" size="sm" /></InputGroup.Text>}
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    
                                                    <hr className="my-4" />
                                                    
                                                    <h6 className="text-primary fw-bold mb-3">
                                                        <FaImage className="me-2" /> Images & Videos
                                                    </h6>
                                                    
                                                    <Row className="mb-4">
                                                        <Col md={6}>
                                                            <Form.Group controlId="images">
                                                                <Form.Label className="fw-bold">Upload Images</Form.Label>
                                                                <InputGroup className="mb-3">
                                                                    <InputGroup.Text><FaImage /></InputGroup.Text>
                                                                    <Form.Control 
                                                                        type="file" 
                                                                        multiple 
                                                                        onChange={handleFileChange}
                                                                        disabled={!selectedCarModel || formLoading} 
                                                                    />
                                                                </InputGroup>
                                                                
                                                                {previewUrls.length > 0 && (
                                                                    <div className="mt-3 p-2 border rounded bg-light">
                                                                        <p className="small text-muted mb-2">Image Previews:</p>
                                                                        <div className="d-flex flex-wrap">
                                                                            {previewUrls.map((url, index) => (
                                                                                <div key={index} className="position-relative me-2 mb-2">
                                                                                    <img 
                                                                                        src={url} 
                                                                                        alt={`Preview ${index + 1}`} 
                                                                                        className="rounded" 
                                                                                        style={{ width: "80px", height: "80px", objectFit: "cover" }} 
                                                                                    />
                                                                                    <Badge 
                                                                                        bg="info" 
                                                                                        className="position-absolute top-0 end-0"
                                                                                    >
                                                                                        {index + 1}
                                                                                    </Badge>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Form.Group>
                                                        </Col>
                                                        
                                                        <Col md={6}>
                                                            <Form.Group controlId="videos">
                                                                <Form.Label className="fw-bold">Upload Videos</Form.Label>
                                                                <InputGroup className="mb-3">
                                                                    <InputGroup.Text><FaVideo /></InputGroup.Text>
                                                                    <Form.Control 
                                                                        type="file" 
                                                                        accept=".mp4,.avi,.mkv"
                                                                        multiple 
                                                                        onChange={handleVideoChange}
                                                                        disabled={!selectedCarModel || formLoading} 
                                                                    />
                                                                </InputGroup>
                                                                
                                                                {videoFiles.length > 0 && (
                                                                    <div className="mt-3 p-2 border rounded bg-light">
                                                                        <p className="small text-muted mb-2">Selected Videos:</p>
                                                                        <ul className="list-unstyled mb-0">
                                                                            {videoFiles.map((file, index) => (
                                                                                <li key={index}>
                                                                                    <FaPlay className="me-2 text-muted" />
                                                                                    {file.name} ({Math.round(file.size / 1024)} KB)
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    
                                                    <hr className="my-4" />
                                                    
                                                    <h6 className="text-primary fw-bold mb-3">
                                                        <FaTags className="me-2" /> Pricing Information
                                                    </h6>
                                                    
                                                    <Row className="mb-3">
                                                        <Col md={5}>
                                                            <Form.Group controlId="planName">
                                                                <Form.Label>Plan Name</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={planName}
                                                                    onChange={(e) => setPlanName(e.target.value)}
                                                                    placeholder="e.g. Basic, Standard, Premium"
                                                                    disabled={!selectedCarModel || formLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Group controlId="planDuration">
                                                                <Form.Label>Duration</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={planDuration}
                                                                    onChange={(e) => setPlanDuration(e.target.value)}
                                                                    placeholder="e.g. 1 year, 6 months"
                                                                    disabled={!selectedCarModel || formLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={2}>
                                                            <Form.Group controlId="price">
                                                                <Form.Label>Price</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text>$</InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={price}
                                                                        onChange={(e) => setPrice(e.target.value)}
                                                                        placeholder="0.00"
                                                                        disabled={!selectedCarModel || formLoading}
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={2} className="d-flex align-items-end">
                                                            <Button 
                                                                variant="outline-primary" 
                                                                onClick={handleAddPricing}
                                                                className="w-100"
                                                                disabled={!selectedCarModel || formLoading || !planName || !planDuration || !price}
                                                            >
                                                                <FaPlus className="me-2" /> Add Plan
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                    
                                                    {pricing.length > 0 && (
                                                        <div className="mt-3 mb-4">
                                                            <p className="fw-bold">Added Price Plans:</p>
                                                            <div className="table-responsive">
                                                                <Table className="table-sm">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th>Plan Name</th>
                                                                            <th>Duration</th>
                                                                            <th>Price</th>
                                                                            <th>Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {pricing.map((priceItem, index) => (
                                                                            <tr key={index}>
                                                                                <td>{priceItem.plan_name}</td>
                                                                                <td>{priceItem.plan_duration}</td>
                                                                                <td>${priceItem.price}</td>
                                                                                <td>
                                                                                    <Button 
                                                                                        variant="outline-danger" 
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            setPricing(pricing.filter((_, i) => i !== index));
                                                                                        }}
                                                                                    >
                                                                                        <FaTrash />
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="d-flex justify-content-end mt-4">
                                                        <Button 
                                                            variant="primary" 
                                                            type="submit"
                                                            className="px-4"
                                                            disabled={formLoading || !selectedCarModel || (files.length === 0 && videoFiles.length === 0 && pricing.length === 0)}
                                                        >
                                                            {formLoading ? (
                                                                <>
                                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                                    Uploading...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaUpload className="me-2" /> Upload Assets
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            </Card.Body>
                                        </Card>
                                    </Tab>
                                    
                                    <Tab eventKey="pricing" title={<span><FaTags className="me-2" />Pricing</span>}>
                                        <Card className="border-0 shadow-sm">
                                            <Card.Body>
                                                {dataLoading ? (
                                                    <div className="text-center py-5">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-3 text-muted">Loading pricing data...</p>
                                                    </div>
                                                ) : pricing.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <Table striped bordered hover>
                                                            <thead>
                                                                <tr>
                                                                    <th width="45%">Plan Name</th>
                                                                    <th width="25%">Duration</th>
                                                                    <th width="15%">Price</th>
                                                                    <th width="15%">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentPricing.map((priceItem) => (
                                                                    <tr key={priceItem.id}>
                                                                        <td className="fw-bold">{priceItem.plan_name}</td>
                                                                        <td>{priceItem.plan_duration}</td>
                                                                        <td className="fw-bold text-success">${priceItem.price}</td>
                                                                        <td>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Edit Plan</Tooltip>}
                                                                            >
                                                                                <Button
                                                                                    variant="outline-primary"
                                                                                    size="sm"
                                                                                    className="me-2"
                                                                                    onClick={() => handleEditPricing(priceItem)}
                                                                                    disabled={loading}
                                                                                >
                                                                                    <FaEdit />
                                                                                </Button>
                                                                            </OverlayTrigger>
                                                                            
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Delete Plan</Tooltip>}
                                                                            >
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => handleShowDeleteConfirm(priceItem, "price")}
                                                                                    disabled={loading}
                                                                                >
                                                                                    <FaTrash />
                                                                                </Button>
                                                                            </OverlayTrigger>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                        
                                                        {renderPagination(currentPage, totalPricePages, setCurrentPage)}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <FaInfoCircle size={48} className="text-muted mb-3" />
                                                        <h5>No Pricing Plans Available</h5>
                                                        <p className="text-muted">
                                                            Add pricing plans from the "Add Content" tab.
                                                        </p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Tab>
                                    
                                    <Tab eventKey="assets" title={<span><FaImage className="me-2" />Images & Videos</span>}>
                                        <Card className="border-0 shadow-sm mb-4">
                                            <Card.Header className="bg-light">
                                                <h6 className="mb-0 fw-bold">
                                                    <FaImage className="me-2" /> Car Images
                                                </h6>
                                            </Card.Header>
                                            <Card.Body>
                                                {dataLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-3 text-muted">Loading images...</p>
                                                    </div>
                                                ) : images.length > 0 ? (
                                                    <>
                                                        <div className="row g-3">
                                                            {currentImages.map((image, index) => (
                                                                <div key={index} className="col-md-3 col-sm-4 col-6">
                                                                    <div className="card h-100">
                                                                        <div className="position-relative">
                                                                            <img 
                                                                                src={image.url} 
                                                                                alt={`Car ${index + 1}`} 
                                                                                className="card-img-top" 
                                                                                style={{ height: "160px", objectFit: "cover" }} 
                                                                            />
                                                                            <div className="position-absolute top-0 end-0 p-2">
                                                                                <Button 
                                                                                    variant="danger" 
                                                                                    size="sm" 
                                                                                    className="rounded-circle" 
                                                                                    onClick={() => handleShowDeleteConfirm(image, "image")}
                                                                                >
                                                                                    <FaTrash />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="card-footer bg-light">
                                                                            <small className="text-muted">Image #{indexOfFirstImage + index + 1}</small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {renderPagination(currentImagePage, totalImagePages, setCurrentImagePage)}
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <FaInfoCircle size={48} className="text-muted mb-3" />
                                                        <h5>No Images Available</h5>
                                                        <p className="text-muted">
                                                            Upload images from the "Add Content" tab.
                                                        </p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                        
                                        <Card className="border-0 shadow-sm">
                                            <Card.Header className="bg-light">
                                                <h6 className="mb-0 fw-bold">
                                                    <FaVideo className="me-2" /> Car Videos
                                                </h6>
                                            </Card.Header>
                                            <Card.Body>
                                                {dataLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-3 text-muted">Loading videos...</p>
                                                    </div>
                                                ) : videos.length > 0 ? (
                                                    <>
                                                        <div className="row g-3">
                                                            {currentVideos.map((video, index) => (
                                                                <div key={index} className="col-md-6">
                                                                    <div className="card h-100">
                                                                        <div className="position-relative">
                                                                            <video 
                                                                                controls
                                                                                className="card-img-top"
                                                                                style={{ height: "240px", objectFit: "cover" }}
                                                                            >
                                                                                <source src={video.url} type="video/mp4" />
                                                                                Your browser does not support HTML5 video.
                                                                            </video>
                                                                            <div className="position-absolute top-0 end-0 p-2">
                                                                                <Button 
                                                                                    variant="danger" 
                                                                                    size="sm" 
                                                                                    className="rounded-circle" 
                                                                                    onClick={() => handleShowDeleteConfirm(video, "video")}
                                                                                >
                                                                                    <FaTrash />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="card-footer bg-light">
                                                                            <small className="text-muted">Video #{indexOfFirstVideo + index + 1}</small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {renderPagination(currentVideoPage, totalVideoPages, setCurrentVideoPage)}
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <FaInfoCircle size={48} className="text-muted mb-3" />
                                                        <h5>No Videos Available</h5>
                                                        <p className="text-muted">
                                                            Upload videos from the "Add Content" tab.
                                                        </p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        </Card>
                    </Container>
                </main>
            </div>
            
            {/* Edit Pricing Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaEdit className="me-2" /> Edit Pricing Plan
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="modalPlanName">
                            <Form.Label>Plan Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="planName"
                                value={modalData.planName}
                                onChange={handleModalChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="modalPlanDuration">
                            <Form.Label>Plan Duration</Form.Label>
                            <Form.Control
                                type="text"
                                name="planDuration"
                                value={modalData.planDuration}
                                onChange={handleModalChange}
                            />
                        </Form.Group>
                        <Form.Group controlId="modalPrice">
                            <Form.Label>Price</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    name="price"
                                    value={modalData.price}
                                    onChange={handleModalChange}
                                />
                            </InputGroup>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleModalSubmit}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="me-2" /> Save Changes
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Delete Confirmation Modal */}
            <Modal 
                show={showDeleteModal} 
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3 text-danger">
                        <FaTrash size={48} />
                    </div>
                    <h5>Are you sure you want to delete this {deleteType}?</h5>
                    <p className="text-danger mt-3">
                        This action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteItem}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Car Model Manager Modal */}
            <Modal 
                show={showModelManagerModal} 
                onHide={() => setShowModelManagerModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPlus className="me-2" /> Add New Car Model
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CarModelManager />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModelManagerModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AddImages;